/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Box,
  Divider,
  Flex,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Scrollbar,
  Space,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useToggle } from '@tonic-one/react-hooks';
import CSpinner from '../../../components/CSpinner';
import { useEffect, useRef, useState } from 'react';
import { Steps } from 'antd';
import {
  DataSyncFailed,
  DataSynced,
  DataSyncing,
  EnablePolicyRadioGroup,
  ProvisionOrgInfo,
  StepIcon,
  UserIcon,
} from './share';
import { useTranslation } from 'react-i18next';
import { deleteCookie, getProvisionCookie } from '../../../utils/common';
import { ProvisionApi } from '@Apis';
import {
  AuthenticationInfoRes,
  EnabledPolicyTypeEnum,
  IdSyncStatus,
  ProvisionModalState,
  ProvisionStatusEnum,
  TokenStatus,
} from '../../../types/provision';
import ErrorAlert from '../../../components/Common/ErrorAlert';
import { reportPendo } from '../../../utils/setupPendo';
import { Service } from '@Types/app';

enum SfStatus {
  NotProvision = 0,
  Provisioning = 1,
  Provisioned = 2,
}

export type SfProvisionModalState = ProvisionModalState & {
  service: Service.SalesforceProduction | Service.SalesforceSandbox;
};

const cookieMap = {
  [Service.SalesforceProduction]: 'provision_status_salesforce_production',
  [Service.SalesforceSandbox]: 'provision_status_salesforce_sandbox',
} as const;

const i18nKeyMap = {
  [Service.SalesforceProduction]: 'sfProduction',
  [Service.SalesforceSandbox]: 'sfSandbox',
} as const;

const SalesForceModal = ({
  service,
  orgId,
  onClose,
  isOpen,
  provisionStatus,
}: SfProvisionModalState) => {
  const { t } = useTranslation(['provision', 'common']);
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const StatusMap = {
    [ProvisionStatusEnum.NotStarted]: SfStatus.NotProvision,
    [ProvisionStatusEnum.Provisioning]: SfStatus.Provisioning,
    [ProvisionStatusEnum.Provisioned]: SfStatus.Provisioned,
  };

  const [syncDataFailFlag, setSyncDataFailFlag] = useState(
    provisionStatus.status === ProvisionStatusEnum.Failure &&
      [IdSyncStatus.Failure, 'FAILURE'].includes(
        provisionStatus?.idSync?.status
      )
  );
  const [sfStatus, setSfStatus] = useState<SfStatus>(() => {
    if (Object.keys(StatusMap).includes(provisionStatus.status)) {
      return StatusMap[provisionStatus.status as keyof typeof StatusMap];
    } else {
      //provisionStatus.status = failure
      //token failure
      if (provisionStatus.authorization.status === TokenStatus.Failure) {
        return SfStatus.NotProvision;
      } else {
        //id sync fail
        return SfStatus.Provisioning;
      }
    }
  });
  const [authenticationInfo, setAuthenticationInfo] =
    useState<null | AuthenticationInfoRes>(null);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (sfStatus === SfStatus.NotProvision) {
      ProvisionApi.apiGetAuthenticationInfo(service, orgId).then((res) => {
        setAuthenticationInfo(res.data);
      });
    }
  }, []);

  return (
    <Modal isOpen={isOpen} isClosable onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t(`provision.service.${i18nKeyMap[service]}.title`)}
        </ModalHeader>
        <Scrollbar overflow="scroll" maxHeight="60vh">
          <ModalBody mt="0px !important">
            <ProvisionOrgInfo orgId={orgId} />

            {errorMsg && (
              <ErrorAlert
                errorMsg={errorMsg}
                onClose={() => setErrorMsg(undefined)}
              />
            )}
            <Flex direction="column" gap="16px">
              <Steps
                direction="vertical"
                current={sfStatus}
                items={[
                  {
                    title: (
                      <Text>
                        {t('provision.service.sf.installApp')}{' '}
                        <Link
                          display="inline"
                          onClick={() => {
                            window.open(
                              authenticationInfo?.[0]?.installAppUrl || '',
                              '_blank'
                            );
                          }}
                        >
                          {t('common.action.label.clickHere')}
                        </Link>
                      </Text>
                    ),
                    description: <Space height="16px" />,
                    icon: <UserIcon />,
                  },
                  {
                    title: (
                      <Text color={colorStyle.color.primary}>
                        {t('provision.service.sf.grantPermission')}
                      </Text>
                    ),
                    description:
                      sfStatus === SfStatus.NotProvision ? (
                        <GrantPermissionAndEnablePolicy
                          service={service}
                          setSfStatus={setSfStatus}
                          authenticationInfo={authenticationInfo}
                          setErrorMsg={setErrorMsg}
                          orgId={orgId}
                        />
                      ) : (
                        <Text color={colorStyle.color.tertiary}>
                          {t('provision.common.permissionGranted')}
                        </Text>
                      ),
                    icon: (
                      <StepIcon checked={sfStatus >= SfStatus.Provisioning} />
                    ),
                  },
                ]}
              />
              {sfStatus === SfStatus.Provisioning &&
                (syncDataFailFlag ? (
                  <DataSyncFailed
                    service={service}
                    onRetry={async () => {
                      await ProvisionApi.apiRetrySyncData(service, orgId);
                      setSfStatus(SfStatus.Provisioning);
                      setSyncDataFailFlag(false);
                    }}
                  />
                ) : (
                  <DataSyncing
                    onRefresh={async () => {
                      const res = await ProvisionApi.apiGetProvisionStatus(
                        service,
                        orgId
                      );
                      if (res.data.status === ProvisionStatusEnum.Provisioned) {
                        setSfStatus(SfStatus.Provisioned);
                      }
                    }}
                  />
                ))}
              {sfStatus === SfStatus.Provisioned && <DataSynced />}
            </Flex>
          </ModalBody>
        </Scrollbar>
        <ModalFooter>
          <Button minWidth="20x" onClick={onClose}>
            {t('common.action.label.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SalesForceModal;

const GrantPermissionAndEnablePolicy = ({
  service,
  authenticationInfo,
  setSfStatus,
  setErrorMsg,
  orgId,
}: {
  service: Service.SalesforceProduction | Service.SalesforceSandbox;
  authenticationInfo: AuthenticationInfoRes | null;
  setSfStatus: (status: SfStatus) => void;
  setErrorMsg: (msg: string | undefined) => void;
  orgId: string;
}) => {
  const { t } = useTranslation('provision');
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [loading, toggleLoading] = useToggle(false);
  const radioGroupRef = useRef<{
    enabledPolicyType: EnabledPolicyTypeEnum;
  }>({
    enabledPolicyType: EnabledPolicyTypeEnum.Default,
  });
  const timeRef = useRef<any>(null);
  const onClick = async () => {
    const authUrl = authenticationInfo?.[0]?.authenticationUrl;
    if (!authUrl) {
      return;
    }
    reportPendo('Provision.GrantPermission.Click', {
      orgId,
      service,
    });
    setErrorMsg(undefined);
    toggleLoading(true);
    window.open(authUrl, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      const sfProvisionInfo = getProvisionCookie(cookieMap[service]);
      if (sfProvisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        const { error, code, state } = JSON.parse(sfProvisionInfo);
        if (error) {
          setErrorMsg(
            t(`provision.service.${i18nKeyMap[service]}.error.deniedGrant`)
          );
        } else {
          try {
            const res = await ProvisionApi.apiSaveProvisionInfoForStorage(
              service,
              {
                authCode: code,
                csrfCode: state,
              },
              radioGroupRef.current.enabledPolicyType,
              orgId
            );
            if (res.data?.status === 'failure') {
              setErrorMsg(
                t([
                  `provision.service.${i18nKeyMap[service]}.error.saveInfo.${res.data?.reason}`,
                  'common.apiException.internalError',
                ])
              );
            } else {
              setSfStatus(SfStatus.Provisioning);
            }
          } catch (e) {
            setErrorMsg(t('common.apiException.internalError'));
            console.error(JSON.stringify(e));
          }
        }
        toggleLoading(false);
      }
    }, 1000);
  };
  useEffect(() => {
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    return () => {
      if (timeRef.current) {
        clearInterval(timeRef.current);
      }
    };
  }, []);

  return (
    <>
      {loading && <CSpinner />}
      <Flex direction="column" gap="16px" color={colorStyle.color.primary}>
        <Space />
        <Divider variant="dashed" orientation="horizontal" />
        <Text color={colorStyle.color.secondary}>
          {/* TODO: i18n "Select the policy to enable when the access grant is completed:" */}
          {t('provision.common.selectPolicyToEnable')}
        </Text>
        <EnablePolicyRadioGroup service={service} ref={radioGroupRef} />
        <Divider variant="dashed" orientation="horizontal" />

        <Box>
          <Button variant="secondary" borderRadius="2rem" onClick={onClick}>
            {t('provision.button.grantPermission')}
          </Button>
        </Box>
      </Flex>
    </>
  );
};
